// Inbound tracking (N7): Move to → Shipped asks how it travels (carrier, tracking number, the day it shipped; all
// optional), and the PO and receiving show it with a link to the carrier's tracking page where one is known.
import { ExternalLink, Truck } from "lucide-react";
import { useState } from "react";
import { planToday } from "../../../../shared/day.ts";
import type { Po } from "../../../../shared/db.gen.ts";
import { CARRIERS, trackingUrl } from "../../../../shared/po.ts";
import { dayText } from "../../lib/format.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { Button, cx, Input } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";

/** "DHL 1234567890 ↗ · shipped Oct 28": nothing when there's no tracking. */
export function TrackingText({ po, className }: { po: Pick<Po, "carrier" | "tracking_number" | "shipped_on">; className?: string }) {
  if (!po.carrier && !po.tracking_number && !po.shipped_on) return null;
  const url = trackingUrl(po.carrier, po.tracking_number);
  const label = [po.carrier, po.tracking_number].filter(Boolean).join(" ");
  return (
    <span className={cx("inline-flex min-w-0 items-center gap-1", className)}>
      <Truck className="size-3.5 shrink-0 text-faint" />
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex min-w-0 items-center gap-0.5 truncate text-accent-text hover:underline"
          title={`Track it on ${po.carrier}'s site`}
        >
          {label} <ExternalLink className="size-3 shrink-0" />
        </a>
      ) : (
        label && <span className="truncate">{label}</span>
      )}
      {po.shipped_on && (
        <span className="shrink-0 text-faint">
          {label ? "· " : ""}shipped {dayText(po.shipped_on)}
        </span>
      )}
    </span>
  );
}

/** Move to → Shipped: how it travels, then the status moves (one mutation). Skip moves it without tracking. */
export function ShippedDialog({ po, onClose }: { po: Po; onClose: () => void }) {
  const [carrier, setCarrier] = useState(po.carrier);
  const [number, setNumber] = useState(po.tracking_number);
  const [shipped, setShipped] = useState(po.shipped_on ?? planToday());
  const [busy, setBusy] = useState(false);
  const save = async (withTracking: boolean) => {
    const shipping = withTracking ? { carrier: carrier.trim(), tracking_number: number.trim(), shipped_on: shipped || null } : undefined;
    setBusy(true);
    const { ok } = await mutateWithResult("po.set_status", { id: po.id, status: "shipped", ...(shipping ? { shipping } : {}) }, [
      { t: "po", id: po.id, row: { ...po, status: "shipped", ...(shipping ?? {}) } },
    ]);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="w-[440px] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="text-[15px] font-semibold">{po.number} shipped</div>
        <p className="mt-1 text-[12px] text-muted">How it travels, so receiving knows what's coming. All optional; change them on the PO later.</p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Carrier</span>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} list="hhl-carriers" placeholder="e.g. DHL" autoFocus />
            <datalist id="hhl-carriers">
              {CARRIERS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Shipped on</span>
            <Input type="date" value={shipped} onChange={(e) => setShipped(e.target.value)} />
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-[12px] text-muted">Tracking number</span>
            <Input value={number} onChange={(e) => setNumber(e.target.value)} className="font-mono" placeholder="From the vendor's shipping notice" />
          </label>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button variant="ghost" onClick={() => save(false)} disabled={busy} title="Move it to Shipped without tracking">
            Skip
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" icon={<Truck className="size-3.5" />} disabled={busy}>
            Mark shipped
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}

/** Labels and printouts --- a PO's label choices (one label per line), for the PO panel and receiving. */
export const poLabels = (poId: string) => [
  { label: "Item labels", hint: "2.25 × 1.25 in, one per line", href: `/api/labels/po/${poId}` },
  { label: "Bag labels", hint: "4 × 2 in, one per line, with the PO number and a box for the quantity", href: `/api/labels/po/${poId}?kind=bag` },
  {
    label: "Bag labels with quantities",
    hint: "Each line's quantity still due printed (lines all here left out)",
    href: `/api/labels/po/${poId}?kind=bag&qty=1`,
  },
];
